import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Sun, Target, RefreshCw, Rocket, ChevronRight } from 'lucide-react';

interface PlannerStep1GoalProps {
  initialIdea: string;
  initialGoal: string;
  initialName: string;
  onNext: (data: { idea: string; goal: string; name: string }) => void;
  isLoading: boolean;
}

const templates = [
  { id: 'seasonal', icon: Sun, label: 'Seasonal Promo', desc: 'Holiday, seasonal or event-based campaigns', example: 'A summer sale for our delivery service' },
  { id: 'lead_gen', icon: Target, label: 'Lead Generation', desc: 'Attract new customers and grow your audience', example: 'Get more sign-ups for our free trial' },
  { id: 'retention', icon: RefreshCw, label: 'Customer Retention', desc: 'Re-engage lapsed or existing customers', example: 'Win back customers who haven\'t ordered in 3 months' },
  { id: 'product_launch', icon: Rocket, label: 'Product Launch', desc: 'Announce and promote a new product or service', example: 'Launching our new express delivery tier' },
];

export const PlannerStep1Goal: React.FC<PlannerStep1GoalProps> = ({ initialIdea, initialGoal, initialName, onNext, isLoading }) => {
  const [idea, setIdea] = useState(initialIdea);
  const [goal, setGoal] = useState(initialGoal);
  const [name, setName] = useState(initialName);

  const handleTemplateClick = (t: typeof templates[0]) => {
    setGoal(t.id);
    if (!idea) setIdea(t.example);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Campaign Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spring Sale 2026" />
      </div>

      <div className="space-y-2">
        <Label>What are you planning?</Label>
        <Textarea
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          placeholder="Describe your campaign idea... e.g. 'A spring sale for my courier business with 20% off first deliveries'"
          className="min-h-[100px]"
        />
      </div>

      <div className="space-y-2">
        <Label>Quick-start templates</Label>
        <div className="grid grid-cols-2 gap-3">
          {templates.map((t) => (
            <Card
              key={t.id}
              className={`cursor-pointer transition-all hover:shadow-sm ${goal === t.id ? 'ring-2 ring-primary border-primary' : ''}`}
              onClick={() => handleTemplateClick(t)}
            >
              <CardContent className="p-3 flex items-start gap-3">
                <t.icon className="h-5 w-5 text-teal-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">{t.label}</p>
                  <p className="text-xs text-muted-foreground">{t.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => onNext({ idea, goal, name })} disabled={!idea.trim() || isLoading} className="gap-2">
          {isLoading ? 'Saving...' : 'Generate Strategy'}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
